/**
 * AIChatWidget — глобальный AI-ассистент в правом нижнем углу.
 *
 * Открывается из топбарного меню «Помощники» (AssistantsMenu) через
 * window event `onboardix:ai-assistant:open`. FAB-кнопка в правом нижнем
 * углу удалена 2026-05-23 — четыре конкурирующих FAB-а перекрывали sidebar/
 * контент (см. PR #1088). Industry pattern: Microsoft Copilot / ServiceNow /
 * Atlassian Intelligence — topbar dropdown + right-side drawer.
 *
 * UI: единые conversation/* примитивы (widget variant). Никаких color emoji
 * в content — только Lucide-иконки и текст (см. HARD GATE
 * `feedback_no_color_emoji_in_ui`).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LifeBuoy, Sparkles } from 'lucide-react'

import { apiFetch } from '../../api/client'
import {
    Composer,
    Conversation,
    ConversationHeader,
    EmptyState,
    MessageStream,
} from '../conversation'

const AI_SUGGESTIONS = [
    'Кто в зоне риска увольнения?',
    'Покажи compliance gaps',
    'Сколько дней до дедлайна AML?',
    'Статус OKR команды обмена',
    'Кто не прошёл тренажёр?',
]

const INTRO =
    'Помогу с аналитикой сотрудников, flight-risk, поиском по базе знаний, compliance и статусом OKR.'

const SERVICE_DOWN_MSG =
    'AI-ассистент временно недоступен. Попробуй позже — твой вопрос не потеряется.'

let nextId = 1
const newId = () => `m-${++nextId}-${Date.now()}`

// Собрать источники под ответом: top-3 уникальных по подписи. Каждый —
// { label, href }, где href ведёт на статью Wiki (`/wiki?article=<id>`),
// если бэкенд её отдал (`r.link`); для загруженных файлов href нет → чип
// остаётся текстовым (страницы-просмотра у них нет).
function buildSources(results) {
    const out = []
    const seen = new Set()
    for (const r of results || []) {
        const label = r?.display_name || r?.filename
        if (!label || seen.has(label)) continue
        seen.add(label)
        out.push({ label, href: r?.link || null })
        if (out.length >= 3) break
    }
    return out
}

export default function AIChatWidget() {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined') return
        const onOpen = (event) => {
            if (typeof event.detail?.draft === 'string') setInput(event.detail.draft.slice(0, 4000))
            setOpen(true)
        }
        window.addEventListener('onboardix:ai-assistant:open', onOpen)
        return () => window.removeEventListener('onboardix:ai-assistant:open', onOpen)
    }, [])

    const handleSend = async (text) => {
        if (!text || sending) return
        setInput('')
        setMessages((prev) => [
            ...prev,
            { id: newId(), role: 'user', content: text },
        ])
        setSending(true)
        try {
            const data = await apiFetch('/knowledge/search', {
                method: 'POST',
                body: JSON.stringify({
                    query: text,
                    top_k: 5,
                    min_similarity: 0.2,
                }),
            })
            const sources = buildSources(data?.results)
            const answer =
                data?.ai_answer ||
                (data?.results?.[0]?.content
                    ? `Нашёл в базе знаний:\n\n${data.results[0].content.slice(0, 500)}…`
                    : null) ||
                'Не удалось найти ответ в базе знаний.'
            setMessages((prev) => [
                ...prev,
                {
                    id: newId(),
                    role: 'assistant',
                    content: answer,
                    sources: sources.length ? sources : [{ label: 'База знаний', href: null }],
                },
            ])
        } catch {
            setMessages((prev) => [
                ...prev,
                { id: newId(), role: 'assistant', content: SERVICE_DOWN_MSG },
            ])
        } finally {
            setSending(false)
        }
    }

    const handoffToSupport = () => {
        setOpen(false)
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('onboardix:support:open'))
        }
    }

    return (
        <Conversation
            open={open}
            onClose={() => setOpen(false)}
            variant="widget"
            ariaLabel="AI Assistant"
        >
            <ConversationHeader
                icon={Sparkles}
                title="Onboardix AI"
                subtitle="Ответы по базе знаний компании"
                actions={[
                    {
                        key: 'support',
                        icon: LifeBuoy,
                        label: 'Передать в поддержку',
                        title: 'Передать в поддержку',
                        onClick: handoffToSupport,
                    },
                ]}
                onClose={() => setOpen(false)}
            />

            <MessageStream
                messages={messages}
                sending={sending}
                onSourceClick={(href) => {
                    setOpen(false)
                    navigate(href)
                }}
                emptySlot={
                    <EmptyState
                        heroIcon={Sparkles}
                        heading="Onboardix AI"
                        intro={INTRO}
                        suggestions={AI_SUGGESTIONS.slice(0, 3)}
                        onPick={handleSend}
                        disabled={sending}
                    />
                }
            />

            <Composer
                value={input}
                onChange={setInput}
                onSubmit={handleSend}
                placeholder="Спроси AI…"
                disabled={sending}
                submitOnEnter={true}
                autoFocus={open}
            />
        </Conversation>
    )
}
